import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaAvatar } from "@/shared/components/data-display/ColocaAvatar";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ALL_PHONE_COUNTRIES } from "@/shared/data/phone-countries";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

interface SectionProps {
  id: string;
  eyebrow: string;
  title: string;
  sub?: string;
  children: React.ReactNode;
}

function Section({ id, eyebrow, title, sub, children }: SectionProps) {
  return (
    <section id={id} className="rounded-lg border border-border bg-card p-[22px]">
      <div className="mb-[18px]">
        <p className="font-mono text-[10.5px] tracking-[0.08em] uppercase text-muted-foreground mb-1">
          {eyebrow}
        </p>
        <h2 className="text-base font-semibold tracking-tight text-foreground">{title}</h2>
        {sub && <p className="mt-1 text-[12.5px] text-muted-foreground max-w-[600px]">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

interface FieldProps {
  label: string;
  value: string | undefined;
  fallback: string;
  className?: string;
}

function Field({ label, value, fallback, className }: FieldProps) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <span className="font-mono text-[10.5px] tracking-[0.06em] uppercase text-muted-foreground font-medium">
        {label}
      </span>
      <span className="text-[13px] text-foreground">{value || fallback}</span>
    </div>
  );
}

interface CountryFieldProps {
  label: string;
  iso2: string | undefined;
  fallback: string;
}

function CountryField({ label, iso2, fallback }: CountryFieldProps) {
  const country = iso2 ? ALL_PHONE_COUNTRIES.find((c) => c.iso2 === iso2.toUpperCase()) : null;

  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[10.5px] tracking-[0.06em] uppercase text-muted-foreground font-medium">
        {label}
      </span>
      {country ? (
        <div className="flex items-center gap-2">
          <img
            src={`https://flagcdn.com/${country.iso2.toLowerCase()}.svg`}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="inline-block shrink-0 rounded-[2px] object-cover"
            style={{ width: 22, height: 16 }}
          />
          <span className="text-[13px] text-foreground">{country.name}</span>
        </div>
      ) : (
        <span className="text-[13px] text-foreground">{fallback}</span>
      )}
    </div>
  );
}

export const ProfileViewPage = () => {
  const { user } = useAuth();
  const { t } = useTranslations("profile");

  return (
    <div className="space-y-[22px] p-2">
      <div className="page-head">
        <div>
          <p className="font-mono text-[11px] tracking-[0.06em] uppercase text-muted-foreground mb-1.5">
            {t("eyebrow")}
          </p>
          <h1 className="text-[22px] font-bold tracking-tight text-foreground">{t("page_title")}</h1>
          <p className="text-[13px] text-muted-foreground mt-1">{t("page_sub")}</p>
        </div>
      </div>

      {/* ── Identity ── */}
      <Section id="perfil" eyebrow={t("eyebrow")} title={t("identity_title")} sub={t("identity_sub")}>
        <div className="flex items-center gap-[18px] pb-[18px] border-b border-border">
          <ColocaAvatar src={user?.profile_image} name={user?.full_name} size="lg" />
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold tracking-tight text-foreground">{user?.full_name}</p>
            <p className="text-[12.5px] text-muted-foreground mt-0.5 capitalize">{user?.role}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-14 gap-y-4 pt-4">
          <Field label={t("email_label")} value={user?.email} fallback={t("not_specified")} />
          <Field label={t("phone_label")} value={user?.phone_number} fallback={t("not_specified")} />
          <CountryField label={t("country_label")} iso2={user?.country} fallback={t("not_specified")} />
          <Field label={t("language_label")} value={user?.preferred_language} fallback={t("not_specified")} />
        </div>
      </Section>

      {/* ── Account ── */}
      <Section id="cuenta" eyebrow={t("eyebrow")} title={t("account_section_title")}>
        <div className="space-y-[14px]">
          <div className="flex items-center justify-between py-[2px]">
            <span className="text-[13px] text-muted-foreground">{t("status_label")}</span>
            <ColocaBadge variant="success">{user?.status}</ColocaBadge>
          </div>
          <div className="flex items-center justify-between py-[2px]">
            <span className="text-[13px] text-muted-foreground">{t("two_factor_label")}</span>
            <ColocaBadge variant={user?.two_factor_enabled ? "success" : "secondary"}>
              {user?.two_factor_enabled ? t("two_factor_enabled") : t("two_factor_disabled")}
            </ColocaBadge>
          </div>
          {user?.last_login_at && (
            <div className="flex items-center justify-between py-[2px]">
              <span className="text-[13px] text-muted-foreground">{t("last_login_label")}</span>
              <span className="font-mono text-[12px] text-foreground">
                {new Date(user.last_login_at).toLocaleString("es-ES")}
              </span>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};
